import React, { FC } from 'react';
import { ScrollView, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { EmptyColumnSlot } from './components/EmptyColumnSlot';
import { TraceabilityGraphSkeleton } from './components/TraceabilityGraphSkeleton';
import { TraceabilityNodeCard } from './components/TraceabilityNodeCard';
import { TRACEABILITY_GRAPH_GEOMETRY } from './constants';
import { useManageTraceabilityGraph } from './hooks/useManageTraceabilityGraph';
import { useTraceabilityGraphLayout } from './hooks/useTraceabilityGraphLayout';
import { TraceabilityColumn, TraceabilityGraphProps } from './types';

const TraceabilityGraph: FC<TraceabilityGraphProps> = (props) => {
  const { C } = props;
  const { columns, hasContent, isLoading, normalizeNodeId } = useManageTraceabilityGraph(props);
  const { connectors, onCardLayout, onRowLayout, contentWidth, rowHeight, columnWidth, columnGap } = useTraceabilityGraphLayout(columns);

  if (isLoading) {
    return <TraceabilityGraphSkeleton C={C} columns={columns} />;
  }

  if (!hasContent) {
    return (
      <View
        testID={TEST_IDS.traceabilityGraph.emptyState}
        style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          padding: scale(24),
          backgroundColor: C('Background/Light/Field'),
          borderRadius: scale(8),
        }}
      >
        <Text style={[font.Headline, { color: C('Label/Light/Primary'), textAlign: 'center' }]}>No traceability links found</Text>
        <Text style={[font.Body, { color: C('Label/Light/Secondary'), textAlign: 'center', marginTop: scale(6) }]}>
          This NC file has no linked part, job, batch, machine, or document data yet.
        </Text>
      </View>
    );
  }

  return (
    <View testID={TEST_IDS.traceabilityGraph.container} style={{ flex: 1, alignSelf: 'stretch', minHeight: 0 }}>
      <ScrollView style={{ flex: 1, alignSelf: 'stretch' }} contentContainerStyle={{ flexGrow: 1 }}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'flex-start', paddingHorizontal: scale(20) }}
        >
          <View testID={TEST_IDS.traceabilityGraph.surface} style={{ width: contentWidth, paddingVertical: scale(12) }}>
            {/* Static column headers — carry the stage meaning so the connectors stay label-free. */}
            <View style={{ flexDirection: 'row', gap: columnGap, marginBottom: scale(14) }}>
              {columns.map((column) => (
                <View key={column.id} style={{ width: columnWidth }}>
                  <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Secondary'), textAlign: 'center' }]}>
                    {column.title}
                  </Text>
                </View>
              ))}
            </View>

            <View style={{ position: 'relative' }} onLayout={onRowLayout}>
              {/* Connector overlay sits behind the cards and ignores touches. */}
              <Svg
                width={contentWidth}
                height={rowHeight}
                style={{ position: 'absolute', top: 0, left: 0 }}
                pointerEvents="none"
              >
                {connectors.map((connector) => (
                  <Path
                    key={connector.id}
                    d={connector.path}
                    stroke={C('Link/Light/Primary')}
                    strokeWidth={scale(TRACEABILITY_GRAPH_GEOMETRY.curveStrokeWidth)}
                    strokeOpacity={0.55}
                    strokeLinecap="round"
                    fill="none"
                  />
                ))}
              </Svg>

              <View style={{ flexDirection: 'row', gap: columnGap }}>
                {columns.map((column, columnIndex) => {
                  const prevColumn = columns[columnIndex - 1] as TraceabilityColumn | undefined;
                  const nextColumn = columns[columnIndex + 1] as TraceabilityColumn | undefined;
                  const showLeftPort = !!prevColumn?.nodes.length;
                  const showRightPort = !!nextColumn?.nodes.length;

                  return (
                    <View
                      key={column.id}
                      testID={TEST_IDS.traceabilityGraph.column(column.id)}
                      style={{ width: columnWidth, gap: scale(TRACEABILITY_GRAPH_GEOMETRY.cardGap) }}
                    >
                      {column.nodes.length ? (
                        column.nodes.map((node) => (
                          <TraceabilityNodeCard
                            key={node.nodeId}
                            C={C}
                            node={node}
                            showLeftPort={showLeftPort}
                            showRightPort={showRightPort}
                            normalizeNodeId={normalizeNodeId}
                            onLayout={onCardLayout(node.nodeId)}
                          />
                        ))
                      ) : (
                        <EmptyColumnSlot C={C} loading={column.loading} />
                      )}
                    </View>
                  );
                })}
              </View>
            </View>
          </View>
        </ScrollView>
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(TraceabilityGraph);
